import { Component, Input, Output, EventEmitter } from '@angular/core';
import { SelectItem } from 'primeng/api';
import { TypedSelectChangeEvent } from '@/core/types/angular-primeng-api';

export interface TooltipDropdownOption extends SelectItem {
    tooltip?: string;
    disabledTooltip?: string;
}

/**
 * @class TooltipDropdownComponent
 *
 * @description
 * A wrapper around PrimeNG's `p-dropdown` that adds support for showing tooltips for options.
 * This way a tooltip can for instance explain why options are disabled.
 *
 * Features:
 * - Built on top of PrimeNG `<p-dropdown>`.
 * - Supports all standard inputs like `options`, `optionLabel`, `[(ngModel)]`, `(onChange)`.
 * - Additional optional input `disabledTooltip`: A string displayed as a tooltip for disabled options. Default '' (no tooltip).
 * - Additional optional input `tooltipPosition`: Position of the tooltips (see pTooltip). Default 'right'.
 * - Per-option tooltips (defined in the option object):
 *   - `tooltip`: Shows the tooltip when the option is enabled.
 *   - `disabledTooltip`: shows the tooltip when the option is disabled. Overrides the component level `disabledTooltip`.
 *
 * Attention: You need to set ngDefaultControl, so that the Angular forms API access works as expected. Otherwise, you
 * will get the console error message `ERROR Error: No value accessor for form control with unspecified name attribute`.
 * I was not able to implement the ControlValueAccessor interface for this component; it always ended up in weird behavior.
 *
 * @component
 * @selector app-tooltip-dropdown
 * @example
 * ```html
 * <app-tooltip-dropdown ngDefaultControl
 *    [options]="barchartGroupOptions"
 *    [(ngModel)]="barchartGroupSelected"
 *    optionLabel="label"
 *    (onChange)="changeBarChartGroup()"
 *    disabledTooltip="Purchase a Pro license to use this option."
 *    tooltipPosition="right">
 * </app-tooltip-dropdown>
 * ```
 */
@Component({
    selector: 'app-tooltip-dropdown',
    templateUrl: './tooltip-dropdown.component.html',
    styleUrls: ['./tooltip-dropdown.component.scss'],
    standalone: false,
})
export class TooltipDropdownComponent {
    @Input() options: TooltipDropdownOption[] = [];
    @Input() optionLabel: string = 'label';
    @Input() optionValue: string = 'value';
    @Input() disabledTooltip: string = '';
    @Input() tooltipPosition: string = 'right';
    @Input() ngModel: SelectItem | undefined;
    @Output() ngModelChange = new EventEmitter<SelectItem>();
    @Output() onChange = new EventEmitter<SelectItem>();

    change(event: TypedSelectChangeEvent<SelectItem>): void {
        const option = event.value;
        // In case somehow a click on a disabled condition ended up here, prevent its selection:
        if (option?.disabled) {
            this.ngModelChange.emit(this.ngModel);
            return;
        }
        this.ngModel = option;
        this.ngModelChange.emit(this.ngModel);
        this.onChange.emit(event.value);
    }
}
